// src/components/OrderTracking.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

// shadcn/ui
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge as ShadBadge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

// Icons (lucide-react)
import {
  ShoppingCart,
  Store,
  Bike,
  CheckCheck,
  MessageCircle,
  Phone,
  Smile,
  Headset,
  FileText,
  Timer,
  Star,
} from "lucide-react";

// Existing modals (unchanged)
import ChatModal from "./ChatModal";
import ChatSupportModal from "./ChatSupportModal";
import ReportIncidentModal from "./ReportIncidentModal";

// ⭐ loader-based Maps util
import { loadGoogleMaps } from "../utils/googleMaps";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// Theme tokens
// eslint-disable-next-line no-unused-vars
const DEEP = "#0f6e51";
const PRIMARY = "#13C0A2";
const AMBER = "#FFD43B";

const steps = [
  { label: "Order Placed", icon: ShoppingCart },
  { label: "Processing", icon: Store },
  { label: "Out for Delivery", icon: Bike },
  { label: "Delivered", icon: CheckCheck },
];

const PRESCRIPTION_QUOTE_ACTIONABLE_STATUSES = new Set([
  "pending_user_confirm",
  "price_confirmation_pending",
  "partial_confirmation",
  "pharmacy_confirmation_requested",
  "chemist_confirmed",
]);

// ---------- helpers (same logic) ----------
function formatAddress(address) {
  if (!address) return "";
  if (typeof address === "object") {
    if (address.formatted) {
      return `${address.name ? address.name + ", " : ""}${address.formatted}${
        address.floor ? ", Floor: " + address.floor : ""
      }${address.landmark ? ", " + address.landmark : ""}${
        address.phone ? ", " + address.phone : ""
      }`;
    }
    return `${address.name || ""}, ${address.addressLine || ""}${
      address.floor ? ", Floor: " + address.floor : ""
    }${address.landmark ? ", " + address.landmark : ""}, ${address.phone || ""}`;
  }
  return address;
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// URL + file-type helpers
const toAbsoluteUrl = (u) =>
  !u ? "" : u.startsWith("http") ? u : `${API_BASE_URL}${u}`;
const isPdf = (u = "") => /\.pdf($|\?)/i.test(u);
const isImage = (u = "") => /\.(png|jpe?g|gif|webp|bmp|tiff|svg)($|\?)/i.test(u);

/** Renders a compact grid of prescription files (images + PDFs). */
function PrescriptionFiles({ files = [] }) {
  if (!files.length) return null;
  const list = [...new Set(files.filter(Boolean))];

  return (
    <div className="mt-2 grid grid-cols-3 gap-2">
      {list.map((raw, idx) => {
        const href = toAbsoluteUrl(raw);
        if (!href) return null;

        if (isImage(href) && !isPdf(href)) {
          return (
            <a
              key={idx}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="block"
              title="Open image"
            >
              <img
                src={href}
                alt={`Prescription ${idx + 1}`}
                className="h-24 w-full object-cover rounded-lg border border-emerald-100 shadow-sm"
                loading="lazy"
              />
            </a>
          );
        }

        // PDF (or unknown) → tile link
        return (
          <a
            key={idx}
            href={href}
            target="_blank"
            rel="noreferrer"
            title="Open document"
            className="flex items-center justify-center h-24 w-full rounded-lg border border-emerald-200 bg-emerald-50/60 shadow-sm"
          >
            <div className="text-center px-2">
              <div className="text-emerald-800 font-extrabold text-xs">Document</div>
              <div className="text-[10px] text-emerald-700/80 truncate max-w-[80px] mx-auto">
                {href.split("/").pop()}
              </div>
              <div className="mt-1 text-[10px] font-semibold text-emerald-700">
                {isPdf(href) ? "PDF" : "File"}
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
}

// Lightweight inline toast
function InlineToast({ open, kind = "success", children, onClose }) {
  if (!open) return null;
  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[1200] rounded-xl px-4 py-3 text-sm font-semibold shadow-xl
      ${kind === "error" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}
      onClick={onClose}
    >
      {children}
    </div>
  );
}

// Simple controllable star rating
function Stars({ value = 0, onChange, disabled }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          disabled={disabled}
          onClick={() => onChange && onChange(n)}
          className="disabled:cursor-not-allowed"
        >
          <Star className={`h-5 w-5 ${n <= value ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
}

/** ⭐ Animated inline map: keeps one map instance & smoothly moves driver marker */
function MiniLiveMap({ center, driver }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const driverRef = useRef(null);
  const animRef = useRef(null);

  // init once
  useEffect(() => {
    let mounted = true;
    loadGoogleMaps(["marker", "places"])
      .then((google) => {
        if (!mounted || mapRef.current || !divRef.current) return;
        mapRef.current = new google.maps.Map(divRef.current, {
          center: center || { lat: 28.4595, lng: 77.0266 },
          zoom: 14,
          mapId: "godavaii-map",
          streetViewControl: false,
          mapTypeControl: false,
          gestureHandling: "greedy",
        });
      })
      .catch((e) => console.error("Google Maps failed to load:", e));
    return () => {
      mounted = false;
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // follow center when it changes (first render + if fallback changes)
  useEffect(() => {
    if (!mapRef.current || !center?.lat || !center?.lng) return;
    mapRef.current.setCenter(center);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.lat, center?.lng]);

  // animate driver to new position
  useEffect(() => {
    const gmap = mapRef.current;
    if (!gmap || !driver?.lat || !driver?.lng) return;

    const google = window.google;

    // create marker if missing
    if (!driverRef.current) {
      try {
        const dot = document.createElement("div");
        dot.style.cssText = "width:14px;height:14px;background:#222;border:3px solid #FFD43B;border-radius:50%";
        driverRef.current = new google.maps.marker.AdvancedMarkerElement({
          map: gmap,
          position: driver,
          title: "Delivery Partner",
          content: dot,
        });
      } catch {
        driverRef.current = new google.maps.Marker({
          map: gmap,
          position: driver,
          title: "Delivery Partner",
          label: "🛵",
        });
      }
      gmap.panTo(driver);
      return;
    }

    // smooth interpolate from current -> new
    const from =
      driverRef.current.position && typeof driverRef.current.position.lat === "number"
        ? { lat: driverRef.current.position.lat, lng: driverRef.current.position.lng }
        : {
            lat: driverRef.current.getPosition().lat(),
            lng: driverRef.current.getPosition().lng(),
          };

    const to = { lat: driver.lat, lng: driver.lng };
    const duration = 900;
    const start = performance.now();

    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const lat = from.lat + (to.lat - from.lat) * t;
      const lng = from.lng + (to.lng - from.lng) * t;
      if ("position" in driverRef.current) driverRef.current.position = { lat, lng };
      else driverRef.current.setPosition({ lat, lng });
      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      }
    };
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(step);

    // keep driver in view
    gmap.panTo(to);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.lat, driver?.lng]);

  return (
    <div
      ref={divRef}
      style={{ width: "100%", height: 230, borderRadius: 18, marginTop: 4, overflow: "hidden" }}
    />
  );
}

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const [quoteDialog, setQuoteDialog] = useState(false);
  const [quoteActionLoading, setQuoteActionLoading] = useState(false);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatThread, setChatThread] = useState(null);
  const [chatPartner, setChatPartner] = useState({ name: "", type: "" });

  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [reportIncidentOpen, setReportIncidentOpen] = useState(false);

  const [eta, setEta] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [refreshTick, setRefreshTick] = useState(0);

  const [pharmacyRating, setPharmacyRating] = useState(null);
  const [deliveryRating, setDeliveryRating] = useState(null);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [deliveryBehavior, setDeliveryBehavior] = useState([]);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const [deliveryUnreadCount, setDeliveryUnreadCount] = useState(0);
  const getToken = () => localStorage.getItem("token") || "";

  // ⭐ NEW: live driver point maintained locally (polled from order only)
  const [liveDriverLoc, setLiveDriverLoc] = useState(null);

  // --- INITIAL LOAD (same logic) ---
  useEffect(() => {
    let didCancel = false;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/orders/${orderId}`)
      .then((res) => {
        if (didCancel) return;
        setOrder({ ...res.data, __type: "order" });
        setLoading(false);
        if (res.data.deliveryRating && res.data.pharmacyRating && res.data.deliveryBehavior) {
          setFeedbackSubmitted(true);
        }
        if (res.data.deliveryRating) setDeliveryRating(res.data.deliveryRating);
        if (res.data.pharmacyRating) setPharmacyRating(res.data.pharmacyRating);
      })
      .catch(() => {
        if (didCancel) return;
        axios
          .get(`${API_BASE_URL}/api/prescriptions/order/${orderId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          })
          .then((res) => {
            if (didCancel) return;
            setOrder({ ...res.data, __type: "prescription" });
            setLoading(false);
          })
          .catch(() => {
            if (didCancel) return;
            setOrder(null);
            setLoading(false);
          });
      });
    return () => {
      didCancel = true;
    };
  }, [orderId, quoteActionLoading]);

  // --- POLLING (same logic for order doc) ---
  useEffect(() => {
    if (!order || order.status === "rejected" || order.status === "cancelled") return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const token = getToken();
        const isPrescription = order.__type === "prescription";
        const url = isPrescription
          ? `${API_BASE_URL}/api/prescriptions/order/${orderId}`
          : `${API_BASE_URL}/api/orders/${orderId}`;
        const res = await axios.get(
          url,
          isPrescription && token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
        );
        if (!cancelled) {
          setOrder((prev) => {
            if (!prev || prev.updatedAt !== res.data.updatedAt || prev.status !== res.data.status) {
              return { ...res.data, __type: isPrescription ? "prescription" : "order" };
            }
            return prev;
          });
        }
      } catch {}
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, order && order.status]);

  // ⭐ NEW: poll ONLY when accepted / out_for_delivery / picked_up to keep live point fresh
  useEffect(() => {
    if (!order) return;
    const s = String(order.status || "").toLowerCase();
    const trackable = ["accepted", "out_for_delivery", "picked_up"].includes(s);
    if (!trackable) {
      setLiveDriverLoc(null);
      return;
    }

    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/orders/${order._id}`);
        const dl = data?.driverLocation;
        if (!cancelled && dl && typeof dl.lat === "number" && typeof dl.lng === "number") {
          setLiveDriverLoc({ lat: dl.lat, lng: dl.lng });
        }
      } catch {}
    };

    tick();
    const id = setInterval(tick, 3000); // 3s feels smooth
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?._id, order?.status]);

  // unread count
  useEffect(() => {
    if (!order) return;
    axios
      .get(`${API_BASE_URL}/api/chat/${order._id}/delivery-unread-count`, { withCredentials: true })
      .then((res) => setDeliveryUnreadCount(res.data.unreadCount || 0))
      .catch(() => setDeliveryUnreadCount(0));
  }, [order, refreshTick, chatOpen]);

  // ⭐ ETA (gate on having a live point and valid status)
  useEffect(() => {
    const s = String(order?.status || "").toLowerCase();
    const canEta =
      order &&
      ["accepted", "out_for_delivery", "picked_up"].includes(s) &&
      (liveDriverLoc || order?.driverLocation);

    if (!canEta) {
      setEta("");
      return;
    }

    let cancelled = false;
    const fetchETA = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/orders/${order._id}/eta`);
        if (!cancelled) setEta(res.data.eta || "N/A");
      } catch {
        if (!cancelled) setEta("N/A");
      }
    };

    fetchETA();
    const interval = setInterval(fetchETA, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?._id, order?.status, liveDriverLoc?.lat, liveDriverLoc?.lng]);

  // quote modal toggle
  useEffect(() => {
    if (!order) return setQuoteDialog(false);
    const status = String(order.status || "").toLowerCase();
    if (order.__type === "prescription") {
      const hasQuote =
        !!order?.quote?.items?.length ||
        !!order?.tempQuote?.items?.length ||
        (Array.isArray(order?.quotes) && order.quotes.length > 0);
      setQuoteDialog(hasQuote && PRESCRIPTION_QUOTE_ACTIONABLE_STATUSES.has(status));
      return;
    }
    setQuoteDialog(status === "quoted" || status === "substitute_confirmation_pending");
  }, [order]);

  // clear local active id
  useEffect(() => {
    if (order && (order.status === "delivered" || order.status === "cancelled")) {
      localStorage.removeItem("activeOrderId");
    }
  }, [order]);

  const handleAcceptQuote = async () => {
    setQuoteActionLoading(true);
    try {
      const token = getToken();
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      if (order.__type === "prescription") {
        await axios.put(`${API_BASE_URL}/api/prescriptions/${order._id}/accept`, {}, cfg);
      } else if (String(order.status || "").toLowerCase() === "substitute_confirmation_pending") {
        await axios.put(`${API_BASE_URL}/api/orders/${order._id}/substitute/accept`, {}, cfg);
      } else {
        await axios.put(`${API_BASE_URL}/api/orders/${order._id}/accept`, {}, cfg);
      }
      setQuoteDialog(false);
      window.location.reload();
    } finally {
      setQuoteActionLoading(false);
    }
  };

  const handleRejectQuote = async () => {
    setQuoteActionLoading(true);
    try {
      const token = getToken();
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
      if (order.__type === "prescription") {
        await axios.post(`${API_BASE_URL}/api/prescriptions/respond/${order._id}`, { response: "rejected" }, cfg);
      } else if (String(order.status || "").toLowerCase() === "substitute_confirmation_pending") {
        await axios.put(`${API_BASE_URL}/api/orders/${order._id}/substitute/reject`, {}, cfg);
      } else {
        await axios.put(`${API_BASE_URL}/api/orders/${order._id}/reject`, {}, cfg);
      }
      setQuoteDialog(false);
      window.location.reload();
    } finally {
      setQuoteActionLoading(false);
    }
  };

  const handleRatingSubmit = async () => {
    setRatingSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/orders/${order._id}/ratings`, {
        pharmacyRating,
        deliveryRating,
        deliveryBehavior,
      });
      setSnackbarMsg("Feedback submitted successfully!");
      setSnackbarOpen(true);
      setFeedbackSubmitted(true);
      if (order && order.status === "delivered") {
        localStorage.removeItem("activeOrderId");
      }
    } catch (e) {
      setSnackbarMsg("Failed to submit feedback!");
      setSnackbarOpen(true);
    }
    setRatingSubmitting(false);
  };

  const handleOpenDeliveryChat = () => {
    setChatPartner({
      name: (order?.deliveryPartner && order.deliveryPartner.name) || "Delivery Partner",
      type: "delivery",
    });
    setChatThread("delivery");
    setChatOpen(true);
    axios.patch(`${API_BASE_URL}/api/chat/${order._id}/delivery-chat-seen`, {}, { withCredentials: true });
    setDeliveryUnreadCount(0);
  };

  if (loading)
    return (
      <div className="w-full min-h-[70vh] grid place-items-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-lg font-extrabold"
          style={{ color: PRIMARY }}
        >
          Loading…
        </motion.div>
      </div>
    );

  if (!order)
    return (
      <div className="mt-16 text-center">
        <div className="text-red-600 font-semibold">Order not found</div>
        <Button
          className="mt-3 font-bold"
          style={{ backgroundColor: PRIMARY, color: "#fff" }}
          onClick={() => navigate("/orders")}
        >
          Go to My Orders
        </Button>
      </div>
    );

  if (order.status === "rejected" || order.status === "cancelled") {
    localStorage.setItem(`order:${order._id}:cancelSeen`, "true");
    return (
      <div className="mt-16 text-center">
        <div className="text-red-600 font-semibold">
          This order was {order.status === "rejected" ? "rejected" : "cancelled"} by the pharmacy.
        </div>
        <Button
          className="mt-3 font-bold"
          style={{ backgroundColor: PRIMARY, color: "#fff" }}
          onClick={() => navigate("/orders")}
        >
          Go to My Orders
        </Button>
      </div>
    );
  }

  const statusNormalized = String(order.status || "").toLowerCase();
  const currentStep =
    typeof order.status === "number"
      ? order.status
      : ["placed", "pending", "quoted", "chemist_notified", "chemist_accepted", "price_confirmation_pending", "partial_confirmation", "pharmacy_confirmation_requested", "chemist_confirmed", "admin_review_required"].includes(statusNormalized)
      ? 0
      : ["processing", "order_confirmed", "final_total_locked", "confirmed"].includes(statusNormalized)
      ? 1
      : ["out_for_delivery", "assigned", "accepted", "picked_up", "dispatched"].includes(statusNormalized)
      ? 2
      : ["delivered", "converted_to_order"].includes(statusNormalized)
      ? 3
      : ["rejected", "cancelled", "failed"].includes(statusNormalized)
      ? -1
      : 0;

  const isDelivered = currentStep === 3;

  const stepTimes = [order.placedAt || order.createdAt, order.processingAt, order.outForDeliveryAt, order.deliveredAt];

  const deliveryPartner =
    order.deliveryPartner && typeof order.deliveryPartner === "object"
      ? order.deliveryPartner
      : {
          name: "Delivery Partner",
          avatar: "/images/delivery-partner.png",
          rating: 4.7,
          phone: "******0000",
          vehicle: "Bike",
        };

  // ------ PRESCRIPTION QUOTE (same logic) ------
  let prescriptionQuote = null;
  if (order.__type === "prescription") {
    if (order.quote?.items?.length > 0) {
      prescriptionQuote = order.quote;
    } else if (order.tempQuote?.items?.length > 0) {
      prescriptionQuote = {
        ...order.tempQuote,
        price: order.tempQuote.approxPrice,
        items: order.tempQuote.items,
      };
    } else if (Array.isArray(order.quotes) && order.quotes.length > 0) {
      prescriptionQuote = order.quotes[order.quotes.length - 1];
    }
  }
  const isCatalogSubstitutePending =
    order.__type !== "prescription" && statusNormalized === "substitute_confirmation_pending";
  const substituteProposal = order?.substituteProposal || null;

  return (
    <div className="bg-slate-50 min-h-screen pb-24 pt-3">
      <div className="max-w-md mx-auto px-2 sm:px-4">
        <Card className="border border-emerald-100/70 shadow-sm rounded-3xl">
          <CardContent className="p-4 sm:p-6">
            {/* Title + ETA */}
            <div className="text-center mb-3">
              <h1 className="text-2xl font-black tracking-tight" style={{ color: PRIMARY }}>
                Track Your Order
              </h1>

              {eta && order.status === "out_for_delivery" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 mt-2 text-sm font-extrabold shadow"
                  style={{ backgroundColor: AMBER, color: "#1d4335" }}
                >
                  <Timer className="h-4 w-4" />
                  Arriving in {eta}
                </motion.div>
              )}

              <div className="mt-2 flex items-center justify-center gap-2">
                <ShadBadge className="bg-emerald-50 text-emerald-700 border border-emerald-100 font-bold text-[13px]">
                  Order #{order.id || order._id?.slice(-5)}
                </ShadBadge>
                <ShadBadge
                  className={`font-bold text-[13px] ${
                    isDelivered ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  {order.userStatus || order.publicStatus || order.statusText || steps[currentStep]?.label || "Reviewing availability"}
                </ShadBadge>
              </div>
            </div>

            {/* Stepper */}
            <div className="relative bg-emerald-50/70 rounded-2xl px-3 py-3 mb-4 overflow-hidden">
              <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 h-1.5 bg-emerald-100 rounded-full" />
              <div
                className="absolute left-3 top-1/2 -translate-y-1/2 h-1.5 bg-emerald-600 rounded-full transition-all"
                style={{
                  width:
                    currentStep <= 0 ? "0%" : currentStep === 1 ? "33%" : currentStep === 2 ? "66%" : "100%",
                }}
              />
              <div className="relative grid grid-cols-4 gap-2">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  const active = i <= currentStep;
                  return (
                    <div key={s.label} className="flex flex-col items-center">
                      <motion.div
                        initial={{ scale: 0.9, opacity: 0.65 }}
                        animate={{ scale: active ? 1 : 0.95, opacity: active ? 1 : 0.6 }}
                        className={`grid place-items-center h-9 w-9 rounded-full border text-white shadow-sm ${
                          active ? "bg-emerald-700 border-emerald-700" : "bg-emerald-200 border-emerald-200"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </motion.div>
                      <div
                        className={`mt-1 text-[11px] font-semibold ${
                          i === currentStep ? "text-emerald-800" : active ? "text-emerald-600" : "text-gray-400"
                        } text-center leading-tight`}
                      >
                        {s.label}
                      </div>
                      {stepTimes[i] && (
                        <div className="text-[10px] font-semibold text-emerald-700/80">{formatTimestamp(stepTimes[i])}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pack SLA breach warning — pharmacy taking too long */}
            {order.exceptionMeta?.packSlaBreached && !["delivered", "out_for_delivery", "cancelled"].includes(String(order.status).toLowerCase()) && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3"
              >
                <Timer className="h-5 w-5 text-amber-600 shrink-0" />
                <div>
                  <div className="text-sm font-bold text-amber-800">Packing is taking longer than usual</div>
                  <div className="text-xs text-amber-600">We've notified the pharmacy — your order will be ready soon.</div>
                </div>
              </motion.div>
            )}

            {/* Delivery partner (out for delivery) */}
            {order.status === "out_for_delivery" && (
              <div className="mb-3 rounded-2xl border border-emerald-100 bg-white p-3 flex items-center justify-between shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
                <div className="flex items-center gap-3">
                  <div className="h-14 w-14 rounded-full bg-emerald-50 border border-emerald-100 overflow-hidden grid place-items-center">
                    <img
                      src={deliveryPartner.avatar || "/images/delivery-partner.png"}
                      alt="Delivery Partner"
                      className="h-full w-full object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                    {!deliveryPartner.avatar && (
                      <div className="text-emerald-700 font-black">
                        {(deliveryPartner.name || "D").charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[17px] font-extrabold text-emerald-900">
                      {deliveryPartner.name || "Delivery Partner"}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="font-semibold">★ {deliveryPartner.rating || 4.7}</span>
                      <span>•</span>
                      <span>{deliveryPartner.vehicle || "Bike"}</span>
                    </div>
                    <div className="text-[13px] text-gray-500">
                      Phone: {(deliveryPartner.phone ? deliveryPartner.phone : "******").replace(/.(?=.{4})/g, "*")}
                    </div>
                    <div className="mt-0.5 text-emerald-700 font-bold text-sm flex items-center gap-1">
                      Arriving in {eta || "Calculating..."} <Bike className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 ml-2">
                  <Button
                    size="icon"
                    className="rounded-full h-12 w-12 shadow"
                    style={{ backgroundColor: PRIMARY, color: "#fff" }}
                    onClick={() => window.open(`tel:${deliveryPartner.mobile || deliveryPartner.phone || ""}`)}
                  >
                    <Phone className="h-5 w-5" />
                  </Button>

                  <div className="relative">
                    {deliveryUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-red-600 text-white text-[10px] font-bold">
                        {deliveryUnreadCount}
                      </span>
                    )}
                    <Button
                      size="icon"
                      className="rounded-full h-12 w-12 shadow"
                      style={{ backgroundColor: AMBER, color: "#222" }}
                      onClick={handleOpenDeliveryChat}
                    >
                      <MessageCircle className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ORDER SUMMARY (unchanged) */}
            <div className="rounded-xl bg-emerald-50/40 p-3">
              {order.__type === "prescription" ? (
                <>
                  <div className="text-sm font-bold">
                    Pharmacy:
                    <span className="text-emerald-700 ml-1">
                      {prescriptionQuote && (prescriptionQuote.pharmacyName || prescriptionQuote.pharmacy?.name)
                        ? (prescriptionQuote.pharmacyName || prescriptionQuote.pharmacy?.name)
                        : "Pharmacy not assigned yet"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-700">
                    Deliver to:
                    <span className="text-emerald-700 ml-1">{formatAddress(order.address)}</span>
                  </div>
                  <div className="text-sm text-slate-700">
                    Amount: <b>₹{prescriptionQuote ? (prescriptionQuote.price || prescriptionQuote.approxPrice || 0) : 0}</b>
                  </div>
                  <div className="text-sm text-slate-500">
                    Items:
                    {prescriptionQuote?.items?.length > 0 ? (
                      prescriptionQuote.items.map((item, i) => (
                        <span key={i} className="text-emerald-700 font-semibold">
                          {" "}
                          {item.medicineName} x{item.quantity || 1} [{item.available !== false ? "Available" : "Unavailable"}];
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400"> Quote not received yet</span>
                    )}
                  </div>
                  {(order.prescriptionUrl || (Array.isArray(order.attachments) && order.attachments.length)) && (
  <div className="mt-3">
    <div className="text-sm text-emerald-700 font-semibold mb-1 flex items-center gap-1">
      <FileText className="h-4 w-4" />
      Prescription Uploaded:
    </div>

    <PrescriptionFiles
      files={[
        ...(Array.isArray(order.attachments) ? order.attachments : []),
        ...(order.prescriptionUrl ? [order.prescriptionUrl] : []),
      ]}
    />
  </div>
)}

                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      className="font-semibold"
                      style={{ borderColor: "#0ea5a2", color: "#0b7669" }}
                      onClick={() => setSupportChatOpen(true)}
                    >
                      <Headset className="h-4 w-4 mr-2" />
                      Contact Support
                    </Button>
                    <Button
                      variant="outline"
                      className="font-semibold"
                      style={{ borderColor: "#ef4444", color: "#dc2626" }}
                      onClick={() => setReportIncidentOpen(true)}
                    >
                      ⚠️ Report Issue
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold">
                    Pharmacy:
                    <span className="text-emerald-700 ml-1">
                      {order.pharmacyName || (typeof order.pharmacy === "object" ? order.pharmacy?.name : order.pharmacy)}
                    </span>
                  </div>
                  <div className="text-sm text-slate-700">
                    Deliver to:
                    <span className="text-emerald-700 ml-1">{formatAddress(order.address)}</span>
                  </div>
                  <div className="text-sm text-slate-700">
                    Amount: <b>₹{order.amount || order.total || 0}</b>
                  </div>
                  <div className="text-sm text-slate-500">
                    Items:
                    {(order.items || []).map((item, i) => (
                      <span key={i} className="text-emerald-700 font-semibold">
                        {" "}
                        {item.name} x{item.qty || item.quantity};
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      className="font-semibold"
                      style={{ borderColor: "#0ea5a2", color: "#0b7669" }}
                      onClick={() => setSupportChatOpen(true)}
                    >
                      <Headset className="h-4 w-4 mr-2" />
                      Contact Support
                    </Button>
                    <Button
                      variant="outline"
                      className="font-semibold"
                      style={{ borderColor: "#ef4444", color: "#dc2626" }}
                      onClick={() => setReportIncidentOpen(true)}
                    >
                      ⚠️ Report Issue
                    </Button>
                  </div>
                  {order.dosage && (
                    <div className="mt-2 flex items-center text-sm text-emerald-700">
                      <FileText className="h-4 w-4 mr-1" />
                      Dosage: {order.dosage}
                    </div>
                  )}
                  {order.note && (
                    <div className="text-sm text-slate-500">
                      <b>Note:</b> {order.note}
                    </div>
                  )}
                  {order.prescription && (
  <div className="mt-3">
    <div className="text-sm text-emerald-700 font-semibold mb-1 flex items-center gap-1">
      <FileText className="h-4 w-4" />
      Prescription Uploaded:
    </div>

    <PrescriptionFiles
      files={
        Array.isArray(order.prescription)
          ? order.prescription
          : [order.prescription]
      }
    />
  </div>
)}

                </>
              )}
            </div>

            {/* MAP — uses animated MiniLiveMap and the live point */}
            {currentStep >= 2 && currentStep < 3 && (
              <div className="mt-3">
                <div className="text-sm text-emerald-700 mb-1">Live Delivery Location:</div>
                <div className="rounded-2xl overflow-hidden">
                  <MiniLiveMap
                    center={{
                      // Prefer driver; fall back to user's address so map shows while waiting for first ping
                      lat: (liveDriverLoc?.lat ?? order.driverLocation?.lat ?? order.address?.lat ?? 28.4595),
                      lng: (liveDriverLoc?.lng ?? order.driverLocation?.lng ?? order.address?.lng ?? 77.0266),
                    }}
                    driver={liveDriverLoc || order.driverLocation}
                  />
                </div>
                <div className="text-sm font-bold text-emerald-700 mt-2">
                  Estimated time to delivery: {eta || "Calculating..."}
                </div>
              </div>
            )}

            {/* Delivered cheer */}
            {isDelivered && (
              <div className="mt-3 flex items-center justify-center text-center gap-2">
                <Smile className="h-7 w-7" style={{ color: AMBER }} />
                <div className="text-emerald-700 font-extrabold">Fastest medicine, happiest you! Get well soon! 💊❤️</div>
              </div>
            )}

            {/* Ratings / Feedback (unchanged) */}
            {isDelivered && (
              <div className="mt-4">
                {feedbackSubmitted ? (
                  <div className="text-center py-6">
                    <Smile className="h-14 w-14 mx-auto mb-2" style={{ color: "#18c5ad" }} />
                    <div className="text-2xl font-black" style={{ color: "#18c5ad" }}>
                      Thank you for your feedback!
                    </div>
                    <div className="text-slate-600 font-medium">
                      We&apos;re glad you chose GoDavaii. <br />
                      Wishing you a speedy recovery!
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Pharmacy card */}
                    <div className="rounded-2xl border border-emerald-100 bg-white p-3 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
                      <div className="flex items-center gap-3">
                        <div className="h-14 w-14 rounded-full border-2 border-emerald-500 bg-emerald-50 grid place-items-center overflow-hidden">
                          <img
                            src="/images/pharmacy.png"
                            alt="Pharmacy"
                            className="h-full w-full object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-[17px] font-extrabold text-emerald-900">
                            {order.pharmacyName ||
                              (typeof order.pharmacy === "object" ? order.pharmacy?.name : order.pharmacy)}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Stars value={pharmacyRating || 0} onChange={(v) => setPharmacyRating(v)} disabled={ratingSubmitting} />
                            <div className="text-sm font-semibold text-slate-600">Rate Pharmacy</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Delivery card */}
                    <div className="rounded-2xl border border-emerald-100 bg-white p-3 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
                      <div className="flex items-center gap-3">
                        <div className="h-14 w-14 rounded-full border-amber-400 bg-amber-50 grid place-items-center overflow-hidden">
                          <img
                            src={deliveryPartner.avatar || "/images/delivery-partner.png"}
                            alt="Partner"
                            className="h-full w-full object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-[17px] font-extrabold text-emerald-900">
                            {deliveryPartner.name || "Delivery Partner"}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Stars value={deliveryRating || 0} onChange={(v) => setDeliveryRating(v)} disabled={ratingSubmitting} />
                            <div className="text-sm font-semibold text-slate-600">Rate Partner</div>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-2 max-w-[260px]">
                            {["Polite", "Helpful", "Rude"].map((label) => {
                              const selected = deliveryBehavior.includes(label.toLowerCase());
                              const isBad = label === "Rude";
                              return (
                                <button
                                  key={label}
                                  type="button"
                                  onClick={() =>
                                    setDeliveryBehavior((prev) =>
                                      prev.includes(label.toLowerCase())
                                        ? prev.filter((b) => b !== label.toLowerCase())
                                        : [...prev, label.toLowerCase()]
                                    )
                                  }
                                  className={`px-3 py-1.5 text-sm font-bold rounded-full border transition
                                    ${
                                      selected
                                        ? isBad
                                          ? "bg-red-50 text-red-700 border-red-200"
                                          : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                        : "bg-slate-50 text-slate-600 border-slate-200"
                                    }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <Button
                        variant="outline"
                        className="font-semibold mr-2"
                        style={{ borderColor: "#0ea5a2", color: "#0b7669" }}
                        onClick={() => setSupportChatOpen(true)}
                      >
                        <Headset className="h-4 w-4 mr-2" />
                        Contact Support
                      </Button>
                      <Button
                        variant="outline"
                        className="font-semibold mr-2"
                        style={{ borderColor: "#ef4444", color: "#dc2626" }}
                        onClick={() => setReportIncidentOpen(true)}
                      >
                        ⚠️ Report Issue
                      </Button>
                      <Button
                        disabled={!(pharmacyRating && deliveryRating && deliveryBehavior.length > 0) || ratingSubmitting}
                        onClick={handleRatingSubmit}
                        className="font-black text-base px-6 py-5 rounded-xl shadow"
                        style={{ backgroundColor: AMBER, color: "#222" }}
                      >
                        {ratingSubmitting ? "Submitting..." : "Submit Feedback"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Back to orders */}
            <Button
              onClick={() => navigate("/orders")}
              className="w-full mt-4 font-bold text-[16px] rounded-xl"
              style={{ backgroundColor: PRIMARY, color: "#fff" }}
            >
              Back to My Orders
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* QUOTE MODAL (same flow) */}
      <Dialog open={quoteDialog} onOpenChange={setQuoteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isCatalogSubstitutePending ? "Substitute Approval Needed" : "Quote Available"}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700">
            {isCatalogSubstitutePending ? (
              <>
                <div className="mb-2">Pharmacy has suggested substitute medicines for this order:</div>
                {Array.isArray(substituteProposal?.items) && substituteProposal.items.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1">
                    {substituteProposal.items.map((item, idx) => (
                      <li key={idx}>
                        {item?.medicineName || item?.name || "Medicine"}{item?.brand ? ` - ${item.brand}` : ""}
                        {item?.price ? ` - ₹${item.price}` : ""}{item?.quantity ? ` × ${item.quantity}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mb-2">Review substitute message from pharmacy before continuing.</div>
                )}
                {substituteProposal?.message && <div className="mt-1 text-slate-500">Note: {substituteProposal.message}</div>}
              </>
            ) : prescriptionQuote ? (
              <>
                <div className="mb-2">Pharmacy has sent you a quote for your prescription:</div>
                <ul className="list-disc pl-5 space-y-1">
                  {prescriptionQuote.items?.map((item, idx) => (
                    <li key={idx}>
                      {item.medicineName} - {item.brand} - Rs {item.price} x {item.quantity} [
                      {item.available !== false ? "Available" : "Unavailable"}]
                    </li>
                  ))}
                </ul>
                <div className="mt-2 font-bold">Total: Rs {prescriptionQuote.price || prescriptionQuote.approxPrice}</div>
                {prescriptionQuote.message && <div className="mt-1 text-slate-500">Note: {prescriptionQuote.message}</div>}
              </>
            ) : (
              <div className="mb-2">Pharmacy has updated this order. Review and continue.</div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={handleRejectQuote}
              disabled={quoteActionLoading}
            >
              {isCatalogSubstitutePending ? "Reject Substitute" : "Reject"}
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleAcceptQuote}
              disabled={quoteActionLoading}
            >
              {isCatalogSubstitutePending ? "Approve Substitute" : "Accept & Pay"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery chat */}
      <ChatModal
        open={chatOpen && chatThread === "delivery"}
        onClose={() => setChatOpen(false)}
        orderId={order._id}
        thread="delivery"
        orderStatus={order.status}
        partnerName={chatPartner.name}
        partnerType={chatPartner.type}
        currentRole="user"
      />

      {/* Support chat */}
      <ChatSupportModal open={supportChatOpen} onClose={() => setSupportChatOpen(false)} orderId={order._id} />

      {/* Report Incident */}
      <ReportIncidentModal
        open={reportIncidentOpen}
        onClose={() => setReportIncidentOpen(false)}
        orderId={order._id}
        token={getToken()}
      />

      {/* Inline toast */}
      <InlineToast
        open={snackbarOpen}
        kind={snackbarMsg.toLowerCase().includes("fail") ? "error" : "success"}
        onClose={() => setSnackbarOpen(false)}
      >
        {snackbarMsg}
      </InlineToast>
    </div>
  );
}
